import React, { createContext, useContext, useEffect, useState } from "react";
import { http, tokenStore } from "./api";

const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking
  const [ready, setReady] = useState(false);

  useEffect(() => {
    // If returning from Emergent Google OAuth, skip /auth/me — the Login
    // page will exchange the session_id and populate the user itself.
    if (typeof window !== "undefined" && window.location.hash?.includes("session_id=")) {
      setUser(false);
      setReady(true);
      return;
    }
    if (!tokenStore.get()) {
      setUser(false);
      setReady(true);
      return;
    }
    http
      .get("/auth/me")
      .then((r) => setUser(r.data))
      .catch(() => { tokenStore.clear(); setUser(false); })
      .finally(() => setReady(true));
  }, []);

  const login = async (email, password, tenantKey) => {
    const { data } = await http.post("/auth/login", { email, password, tenantKey });
    // 2FA branch: backend returns {requires_2fa, otp_challenge, ...} without
    // a session. The caller shows the OTP form and calls `verifyOtp` next.
    if (data.requires_2fa) {
      return { requires2fa: true, challenge: data.otp_challenge, deliveredTo: data.otp_delivered_to };
    }
    if (data.accessToken) tokenStore.set(data.accessToken);
    setUser(data.user);
    return { user: data.user };
  };

  const verifyOtp = async (challengeToken, code) => {
    const { data } = await http.post("/auth/verify-otp", { challengeToken, code });
    if (data.accessToken) tokenStore.set(data.accessToken);
    setUser(data.user);
    return data.user;
  };

  const resendOtp = async (challengeToken) => {
    const { data } = await http.post("/auth/resend-otp", { challengeToken });
    return data;
  };

  const googleSignIn = async (sessionId) => {
    const { data } = await http.post("/auth/google-session", null, {
      headers: { "X-Session-ID": sessionId },
    });
    if (data.accessToken) tokenStore.set(data.accessToken);
    setUser(data.user);
    return data.user;
  };

  const logout = async () => {
    try { await http.post("/auth/logout"); } catch (_e) {}
    tokenStore.clear();
    setUser(false);
  };

  return (
    <AuthCtx.Provider value={{ user, ready, login, verifyOtp, resendOtp, googleSignIn, logout, setUser }}>
      {children}
    </AuthCtx.Provider>
  );
}

export const useAuth = () => useContext(AuthCtx);

export function formatApiError(detail) {
  if (detail == null) return "Something went wrong.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e?.msg ? e.msg : JSON.stringify(e))).join(" ");
  if (detail?.msg) return detail.msg;
  return String(detail);
}
